"use client";

import { useSearchParams } from "next/navigation";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";
import type { BlogStoryblok, InterviewStoryblok } from "@/component-types-sb";
import { useEffect, useState } from "react";

import { SectionHeader } from "@/components/typography/SectionHeader";
import { ImageCard } from "@/components/layout/ImageCard";
import { ContentCard } from "@/components/layout/ContentCard";
import { Categories } from "@/components/layout/Categories";
import { SearchForm } from "@/components/layout/SearchForm";
import { componentName } from "@/lib/utils/string";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";

type BlogProps = {
  blok: BlogStoryblok;
};

const Blog = ({ blok }: BlogProps) => {
  const searchParams = useSearchParams();
  const query = searchParams.get("category")?.toLocaleLowerCase() || undefined;
  const [interviews, setInterviews] = useState<InterviewStoryblok[]>([]);

  const articles = blok.articles?.filter((article: any) =>
    query
      ? article.content.category?.name?.toLocaleLowerCase() ===
        query.toLocaleLowerCase()
      : true
  );

  // Ueber den gecachten Proxy statt ueber die "use server"-Action.
  //
  // Dependency bewusst `blok._uid` statt `blok`: `blok` ist ein Objekt aus dem
  // Storyblok-Baum und bekommt bei jedem Rerender eine neue Referenz — der
  // Effect lief dadurch mehrfach pro Seitenaufruf.
  useEffect(() => {
    const controller = new AbortController();

    fetchStoryblokList<InterviewStoryblok>(
      { content_type: "interview", per_page: 100 },
      controller.signal,
    )
      .then(setInterviews)
      .catch((err) => {
        if (err?.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, [blok._uid]);

  return (
    <section {...storyblokEditable(blok)}>
      <div className="py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">
        <SectionHeader
          tagline="Blog"
          headline={blok.description || ""}
          center
        />

        <SearchForm fullWidth className="md:mx-48 mb-6" />

        <Categories className="justify-center" />

        <div className="mb-8">
          {blok.body?.map((nestedBlok) => (
            <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
          ))}
        </div>

        {!articles?.length && (
          <p className="text-center text-sm text-gray-500">
            Keine Artikel gefunden
          </p>
        )}

        <div className="break-container bg-white">
          <div className="container max-w-7xl mx-auto grid grid-cols-1 gap-8 lg:grid-cols-3 p-4 md:p-14">
            {articles?.map((article: any, index: number) =>
              [0, 1].includes(index) ? (
                <ContentCard
                  key={article.uuid}
                  tagline={componentName(article.content?.component)}
                  headline={article.content?.title}
                  text={article.content?.description}
                  image={article.content?.image?.filename}
                  href={`/${article.full_slug}`}
                  tags={article.content?.tags}
                  reverse={index === 0}
                  color={index === 0 ? "sky" : "sun"}
                  mask
                  className="col-span-full"
                />
              ) : (
                <ImageCard
                  key={article.uuid}
                  tagline={article.content?.topic}
                  headline={article.content?.title}
                  image={article.content?.image?.filename}
                  href={`/${article.full_slug}`}
                  tags={article.content?.tags}
                  className="col-span-full lg:col-span-1"
                />
              )
            )}
            {interviews?.map((interview) => (
              <ImageCard
                key={interview.uuid}
                tagline={interview.content?.topic}
                headline={interview.content?.title}
                image={interview.content?.image?.filename}
                href={`/${interview.full_slug}`}
                tags={interview.content?.tags}
                className="col-span-full lg:col-span-1"
              />
            ))}
          </div>
        </div>

        <div className="my-8">
          {blok.footer?.map((nestedBlok) => (
            <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
